import React, { useContext, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Editor } from "@tiptap/react";
import EmailBlockTypeEnum from "../../../../models/EmailBlockTypeEnum";
import IEmailBlock, {
    EmailBlockAlign,
    IEmailButtonBlock,
    IEmailColumnsBlock,
    IEmailCustomMessageBlock,
    IEmailDividerBlock,
    IEmailFooterBlock,
    IEmailHeadingBlock,
    IEmailImageBlock,
    IEmailLogoBlock,
    IEmailOrderItemTableBlock,
    IEmailSpacerBlock,
    IEmailTextBlock,
} from "../../../../models/IEmailBlock";
import IEmailMergeVar from "../../../../models/IEmailMergeVar";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import FileUploadResourceTypeEnum from "../../../../models/FileUploadResourceTypeEnum";
import IOnCreateFileUploadDTO from "../../../../models/DTO/IOnCreateFileUploadDTO";
import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import { SocketContext } from "../../../../contexts/SocketContext";
import { blockLabelKey } from "../../../../services/EmailDocumentUtils";
import OwRichTextEditor from "../../../shared/OwRichTextEditor/OwRichTextEditor";
import OwS3Uploader from "../../../shared/OwS3Uploader/OwS3Uploader";
import MergeVarPicker from "./MergeVarPicker";

type Props = {
    block: IEmailBlock;
    mergeVars: IEmailMergeVar[];
    orderItemTableColumns: string[];
    scope: "order" | "account";
    onChange: (patch: Partial<IEmailBlock>) => void;
    onClose: () => void;
    disabled?: boolean;
};

type FieldProps = { id: string; labelKey: string; children: React.ReactNode; hint?: string };

const Field = ({ id, labelKey, children, hint }: FieldProps) => {
    const intl = useIntl();

    return (
        <div className={"mb-2"}>
            <label className={"form-label small mb-0"} htmlFor={id}>
                {intl.formatMessage({ id: labelKey })}
            </label>
            {children}
            {hint ? <div className={"form-text"}>{hint}</div> : null}
        </div>
    );
};

const AlignSelect = ({
    id,
    value,
    onChange,
    disabled,
}: {
    id: string;
    value: EmailBlockAlign;
    onChange: (a: EmailBlockAlign) => void;
    disabled?: boolean;
}) => {
    const intl = useIntl();

    return (
        <select
            id={id}
            className={"form-select form-select-sm"}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value as EmailBlockAlign)}>
            {(["left", "center", "right"] as EmailBlockAlign[]).map((a) => (
                <option key={a} value={a}>
                    {intl.formatMessage({ id: `align_${a}` })}
                </option>
            ))}
        </select>
    );
};

/** Text input that a merge-var picker can append into. */
const VarInput = (props: {
    id: string;
    value: string;
    onChange: (v: string) => void;
    mergeVars: IEmailMergeVar[];
    scope: "order" | "account";
    disabled?: boolean;
    maxLength?: number;
}) => {
    const ref = useRef<HTMLInputElement>(null);

    const insert = (v: string) => {
        const el = ref.current;
        const start = el?.selectionStart ?? props.value.length;
        const end = el?.selectionEnd ?? props.value.length;
        props.onChange(props.value.slice(0, start) + v + props.value.slice(end));
    };

    return (
        <div>
            <input
                ref={ref}
                id={props.id}
                type={"text"}
                className={"form-control form-control-sm"}
                value={props.value}
                maxLength={props.maxLength}
                disabled={props.disabled}
                onChange={(e) => props.onChange(e.target.value)}
            />
            <div className={"d-flex justify-content-end mt-1"}>
                <MergeVarPicker
                    mergeVars={props.mergeVars}
                    scope={props.scope}
                    onInsert={insert}
                    disabled={props.disabled}
                    className={"w-auto"}
                />
            </div>
        </div>
    );
};

/** Rich text with a merge-var picker that inserts at the caret. */
const HtmlEditor = (props: {
    name: string;
    value: string;
    onChange: (html: string) => void;
    mergeVars: IEmailMergeVar[];
    scope: "order" | "account";
    disabled?: boolean;
}) => {
    const editorRef = useRef<Editor | null>(null);

    return (
        <div>
            <OwRichTextEditor
                name={props.name}
                value={props.value}
                disabled={props.disabled}
                editorRef={editorRef}
                fn={(e) => props.onChange(e.target.value)}
            />
            <div className={"d-flex justify-content-end mt-1"}>
                <MergeVarPicker
                    mergeVars={props.mergeVars}
                    scope={props.scope}
                    disabled={props.disabled}
                    className={"w-auto"}
                    onInsert={(v) => {
                        const editor = editorRef.current;

                        if (editor) {
                            editor.chain().focus().insertContent(v).run();
                        } else {
                            props.onChange(`${props.value}${v}`);
                        }
                    }}
                />
            </div>
        </div>
    );
};

const ImageEditor = (props: {
    block: IEmailImageBlock;
    onChange: (patch: Partial<IEmailImageBlock>) => void;
    mergeVars: IEmailMergeVar[];
    scope: "order" | "account";
    disabled?: boolean;
}) => {
    const intl = useIntl();
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext);
    const [uploading, setUploading] = useState(false);
    const onChangeRef = useRef(props.onChange);
    onChangeRef.current = props.onChange;

    // The uploader announces the created attachment over the socket; adopt its public URL.
    useEffect(() => {
        if (!socket) {
            return;
        }

        const onCreated = (data: IOnCreateFileUploadDTO) => {
            if (data?.url) {
                onChangeRef.current({ url: data.url, attachment_id: undefined });
                setUploading(false);
            }
        };

        socket.on(EndpointsEnum.FileUploadOnCreateFileUpload, onCreated);

        return () => {
            socket.removeListener(EndpointsEnum.FileUploadOnCreateFileUpload, onCreated);
        };
    }, [socket]);

    return (
        <>
            {socket && userModel && supplierModel && !props.disabled ? (
                <div className={"mb-2"} onClickCapture={() => setUploading(true)}>
                    <OwS3Uploader
                        socket={socket}
                        userModel={userModel}
                        supplierModel={supplierModel}
                        resourceType={FileUploadResourceTypeEnum.emailTemplateImage}
                        isReadOnly={false}
                        dropOnly={false}
                        imagesOnly={true}
                    />
                    {uploading ? <div className={"form-text"}>{intl.formatMessage({ id: "uploading" })}</div> : null}
                </div>
            ) : null}
            <Field id={"img-url"} labelKey={"image_url"}>
                <input
                    id={"img-url"}
                    type={"url"}
                    className={"form-control form-control-sm"}
                    value={props.block.url || ""}
                    disabled={props.disabled}
                    placeholder={"https://"}
                    onChange={(e) => props.onChange({ url: e.target.value || undefined })}
                />
            </Field>
            {props.block.url ? (
                <img src={props.block.url} alt={props.block.alt} style={{ maxWidth: "100%", maxHeight: 120 }} className={"mb-2"} />
            ) : null}
            <Field id={"img-alt"} labelKey={"image_alt_text"}>
                <input
                    id={"img-alt"}
                    type={"text"}
                    className={"form-control form-control-sm"}
                    value={props.block.alt}
                    maxLength={255}
                    disabled={props.disabled}
                    onChange={(e) => props.onChange({ alt: e.target.value })}
                />
            </Field>
            <div className={"row g-2"}>
                <div className={"col-6"}>
                    <Field id={"img-width"} labelKey={"width_px"}>
                        <input
                            id={"img-width"}
                            type={"number"}
                            min={16}
                            max={800}
                            className={"form-control form-control-sm"}
                            value={props.block.width ?? ""}
                            disabled={props.disabled}
                            onChange={(e) => props.onChange({ width: e.target.value ? Number(e.target.value) : undefined })}
                        />
                    </Field>
                </div>
                <div className={"col-6"}>
                    <Field id={"img-align"} labelKey={"alignment"}>
                        <AlignSelect
                            id={"img-align"}
                            value={props.block.align}
                            disabled={props.disabled}
                            onChange={(align) => props.onChange({ align })}
                        />
                    </Field>
                </div>
            </div>
            <Field id={"img-href"} labelKey={"link_url"} hint={intl.formatMessage({ id: "link_url_hint" })}>
                <VarInput
                    id={"img-href"}
                    value={props.block.href || ""}
                    mergeVars={props.mergeVars.filter((v) => v.var.endsWith("_URL%"))}
                    scope={props.scope}
                    disabled={props.disabled}
                    onChange={(href) => props.onChange({ href: href || undefined })}
                />
            </Field>
        </>
    );
};

/**
 * Edits the selected block's fields. One small editor per block type; all emit a partial
 * patch that the page merges into the document.
 */
const BlockPropertyPanel = (props: Props) => {
    const intl = useIntl();
    const { block, disabled } = props;
    const patch = (p: Partial<IEmailBlock>) => props.onChange(p);
    const urlVars = props.mergeVars.filter((v) => v.var.endsWith("_URL%"));

    const body = () => {
        switch (block.type) {
            case EmailBlockTypeEnum.HEADING: {
                const b = block as IEmailHeadingBlock;
                return (
                    <>
                        <Field id={"h-text"} labelKey={"text"}>
                            <VarInput
                                id={"h-text"}
                                value={b.text}
                                maxLength={500}
                                mergeVars={props.mergeVars}
                                scope={props.scope}
                                disabled={disabled}
                                onChange={(text) => patch({ text })}
                            />
                        </Field>
                        <div className={"row g-2"}>
                            <div className={"col-6"}>
                                <Field id={"h-level"} labelKey={"heading_size"}>
                                    <select
                                        id={"h-level"}
                                        className={"form-select form-select-sm"}
                                        value={b.level}
                                        disabled={disabled}
                                        onChange={(e) => patch({ level: Number(e.target.value) as 1 | 2 | 3 })}>
                                        <option value={1}>{intl.formatMessage({ id: "large" })}</option>
                                        <option value={2}>{intl.formatMessage({ id: "medium" })}</option>
                                        <option value={3}>{intl.formatMessage({ id: "small" })}</option>
                                    </select>
                                </Field>
                            </div>
                            <div className={"col-6"}>
                                <Field id={"h-align"} labelKey={"alignment"}>
                                    <AlignSelect
                                        id={"h-align"}
                                        value={b.align}
                                        disabled={disabled}
                                        onChange={(align) => patch({ align })}
                                    />
                                </Field>
                            </div>
                        </div>
                    </>
                );
            }
            case EmailBlockTypeEnum.TEXT: {
                const b = block as IEmailTextBlock;
                return (
                    <>
                        <HtmlEditor
                            name={"html"}
                            value={b.html}
                            mergeVars={props.mergeVars}
                            scope={props.scope}
                            disabled={disabled}
                            onChange={(html) => patch({ html })}
                        />
                        <Field id={"t-align"} labelKey={"alignment"}>
                            <AlignSelect
                                id={"t-align"}
                                value={b.align || "left"}
                                disabled={disabled}
                                onChange={(align) => patch({ align })}
                            />
                        </Field>
                    </>
                );
            }
            case EmailBlockTypeEnum.IMAGE:
                return (
                    <ImageEditor
                        block={block as IEmailImageBlock}
                        mergeVars={props.mergeVars}
                        scope={props.scope}
                        disabled={disabled}
                        onChange={patch}
                    />
                );
            case EmailBlockTypeEnum.LOGO: {
                const b = block as IEmailLogoBlock;
                return (
                    <>
                        <div className={"form-text mb-2"}>{intl.formatMessage({ id: "logo_block_hint" })}</div>
                        <div className={"row g-2"}>
                            <div className={"col-6"}>
                                <Field id={"logo-width"} labelKey={"width_px"}>
                                    <input
                                        id={"logo-width"}
                                        type={"number"}
                                        min={16}
                                        max={800}
                                        className={"form-control form-control-sm"}
                                        value={b.width}
                                        disabled={disabled}
                                        onChange={(e) => patch({ width: Number(e.target.value) || 200 })}
                                    />
                                </Field>
                            </div>
                            <div className={"col-6"}>
                                <Field id={"logo-align"} labelKey={"alignment"}>
                                    <AlignSelect
                                        id={"logo-align"}
                                        value={b.align}
                                        disabled={disabled}
                                        onChange={(align) => patch({ align })}
                                    />
                                </Field>
                            </div>
                        </div>
                    </>
                );
            }
            case EmailBlockTypeEnum.BUTTON: {
                const b = block as IEmailButtonBlock;
                return (
                    <>
                        <Field id={"btn-label"} labelKey={"button_label"}>
                            <VarInput
                                id={"btn-label"}
                                value={b.label}
                                maxLength={100}
                                mergeVars={props.mergeVars}
                                scope={props.scope}
                                disabled={disabled}
                                onChange={(label) => patch({ label })}
                            />
                        </Field>
                        <Field id={"btn-href"} labelKey={"link_url"} hint={intl.formatMessage({ id: "link_url_hint" })}>
                            <VarInput
                                id={"btn-href"}
                                value={b.href}
                                mergeVars={urlVars}
                                scope={props.scope}
                                disabled={disabled}
                                onChange={(href) => patch({ href })}
                            />
                        </Field>
                        <div className={"row g-2"}>
                            <div className={"col-4"}>
                                <Field id={"btn-align"} labelKey={"alignment"}>
                                    <AlignSelect
                                        id={"btn-align"}
                                        value={b.align}
                                        disabled={disabled}
                                        onChange={(align) => patch({ align })}
                                    />
                                </Field>
                            </div>
                            <div className={"col-4"}>
                                <Field id={"btn-bg"} labelKey={"theme_brand_color"}>
                                    <input
                                        id={"btn-bg"}
                                        type={"color"}
                                        className={"form-control form-control-color"}
                                        value={b.backgroundColor || "#FB951F"}
                                        disabled={disabled}
                                        onChange={(e) => patch({ backgroundColor: e.target.value })}
                                    />
                                </Field>
                            </div>
                            <div className={"col-4"}>
                                <Field id={"btn-color"} labelKey={"theme_text_color"}>
                                    <input
                                        id={"btn-color"}
                                        type={"color"}
                                        className={"form-control form-control-color"}
                                        value={b.textColor || "#FFFFFF"}
                                        disabled={disabled}
                                        onChange={(e) => patch({ textColor: e.target.value })}
                                    />
                                </Field>
                            </div>
                        </div>
                    </>
                );
            }
            case EmailBlockTypeEnum.DIVIDER: {
                const b = block as IEmailDividerBlock;
                return (
                    <div className={"row g-2"}>
                        <div className={"col-6"}>
                            <Field id={"dv-color"} labelKey={"color"}>
                                <input
                                    id={"dv-color"}
                                    type={"color"}
                                    className={"form-control form-control-color"}
                                    value={b.color || "#E0E0E0"}
                                    disabled={disabled}
                                    onChange={(e) => patch({ color: e.target.value })}
                                />
                            </Field>
                        </div>
                        <div className={"col-6"}>
                            <Field id={"dv-thick"} labelKey={"thickness_px"}>
                                <input
                                    id={"dv-thick"}
                                    type={"number"}
                                    min={1}
                                    max={20}
                                    className={"form-control form-control-sm"}
                                    value={b.thickness ?? 1}
                                    disabled={disabled}
                                    onChange={(e) => patch({ thickness: Number(e.target.value) || 1 })}
                                />
                            </Field>
                        </div>
                    </div>
                );
            }
            case EmailBlockTypeEnum.SPACER: {
                const b = block as IEmailSpacerBlock;
                return (
                    <Field id={"sp-h"} labelKey={"height_px"}>
                        <input
                            id={"sp-h"}
                            type={"number"}
                            min={1}
                            max={200}
                            className={"form-control form-control-sm"}
                            value={b.height}
                            disabled={disabled}
                            onChange={(e) => patch({ height: Number(e.target.value) || 1 })}
                        />
                    </Field>
                );
            }
            case EmailBlockTypeEnum.COLUMNS: {
                const b = block as IEmailColumnsBlock;
                const presets: [number, number][] = [
                    [50, 50],
                    [33, 67],
                    [67, 33],
                    [25, 75],
                    [75, 25],
                ];
                const current = (b.widths || [50, 50]).join("/");
                return (
                    <Field id={"cols-widths"} labelKey={"column_widths"}>
                        <select
                            id={"cols-widths"}
                            className={"form-select form-select-sm"}
                            value={current}
                            disabled={disabled}
                            onChange={(e) => patch({ widths: e.target.value.split("/").map(Number) as [number, number] })}>
                            {presets.map((p) => (
                                <option key={p.join("/")} value={p.join("/")}>
                                    {p[0]}% / {p[1]}%
                                </option>
                            ))}
                        </select>
                    </Field>
                );
            }
            case EmailBlockTypeEnum.ORDER_ITEM_TABLE: {
                const b = block as IEmailOrderItemTableBlock;
                const hidden = new Set(b.excludeCols || []);
                return (
                    <>
                        <div className={"form-text mb-1"}>{intl.formatMessage({ id: "order_item_table_columns_hint" })}</div>
                        {props.orderItemTableColumns.map((col) => (
                            <div key={col} className={"form-check"}>
                                <input
                                    id={`col-${col}`}
                                    type={"checkbox"}
                                    className={"form-check-input"}
                                    checked={!hidden.has(col)}
                                    disabled={disabled}
                                    onChange={(e) => {
                                        const next = new Set(hidden);
                                        if (e.target.checked) {
                                            next.delete(col);
                                        } else {
                                            next.add(col);
                                        }
                                        patch({ excludeCols: props.orderItemTableColumns.filter((c) => next.has(c)) });
                                    }}
                                />
                                <label className={"form-check-label"} htmlFor={`col-${col}`}>
                                    {intl.formatMessage({ id: `order_item_col_${col}` })}
                                </label>
                            </div>
                        ))}
                    </>
                );
            }
            case EmailBlockTypeEnum.SHIP_TO:
                return <div className={"form-text"}>{intl.formatMessage({ id: "ship_to_block_hint" })}</div>;
            case EmailBlockTypeEnum.ORDER_DETAILS:
                return <div className={"form-text"}>{intl.formatMessage({ id: "order_details_block_hint" })}</div>;
            case EmailBlockTypeEnum.CUSTOM_MESSAGE: {
                const b = block as IEmailCustomMessageBlock;
                return (
                    <>
                        <div className={"form-text mb-2"}>{intl.formatMessage({ id: "custom_message_block_hint" })}</div>
                        <Field id={"cm-heading"} labelKey={"heading"}>
                            <input
                                id={"cm-heading"}
                                type={"text"}
                                className={"form-control form-control-sm"}
                                value={b.heading || ""}
                                maxLength={200}
                                disabled={disabled}
                                onChange={(e) => patch({ heading: e.target.value || undefined })}
                            />
                        </Field>
                    </>
                );
            }
            case EmailBlockTypeEnum.FOOTER: {
                const b = block as IEmailFooterBlock;
                return (
                    <>
                        <HtmlEditor
                            name={"footer"}
                            value={b.html}
                            mergeVars={props.mergeVars}
                            scope={props.scope}
                            disabled={disabled}
                            onChange={(html) => patch({ html })}
                        />
                        <div className={"form-check mt-2"}>
                            <input
                                id={"ft-links"}
                                type={"checkbox"}
                                className={"form-check-input"}
                                checked={b.showOrderwerksLinks}
                                disabled={disabled}
                                onChange={(e) => patch({ showOrderwerksLinks: e.target.checked })}
                            />
                            <label className={"form-check-label"} htmlFor={"ft-links"}>
                                {intl.formatMessage({ id: "show_orderwerks_links" })}
                            </label>
                        </div>
                    </>
                );
            }
            default:
                return null;
        }
    };

    return (
        <div className={"BlockPropertyPanel"} data-testid={"block-property-panel"}>
            <div className={"panel-title"}>
                <strong>{intl.formatMessage({ id: blockLabelKey(block.type) })}</strong>
                <button type={"button"} className={"btn btn-sm btn-link"} onClick={props.onClose}>
                    {intl.formatMessage({ id: "close" })}
                </button>
            </div>
            {body()}
        </div>
    );
};

export default BlockPropertyPanel;
